/**
 * Prefill Gate 1 API / DB forms from intake `captured` FR answers + gate0_data.
 * Never overwrites fields already set in gate0 / form — only fills blanks.
 */

import { captureValue } from "./readinessExtractor";
import {
  EMPTY_API_FORM,
  apiFormFromGate0Data,
  normalizeEndpointMethod,
  resolveEndpointOperation,
} from "./apiSurfaceForm";
import { dbFormFromGate0Data } from "./dbSurfaceForm";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];

const FR_API_EXPOSED = "FR_S6_Q01";
const FR_AUTH = "FR_S6_Q03";
const FR_DATA_RETENTION = "FR_S4_Q03";
const FR_DATA_INPUTS = "FR_S4_Q01";

function isBlank(v) {
  return !String(v ?? "").trim();
}

function apiFormIsEmpty(form) {
  if (!form) return true;
  if (String(form.baseUrl || "").trim()) return false;
  if (String(form.authType || "").trim()) return false;
  const eps = Array.isArray(form.endpoints) ? form.endpoints : [];
  return !eps.some((ep) => String(ep?.path || "").trim());
}

function dbFormIsEmpty(form) {
  if (!form) return true;
  return (
    isBlank(form.host) &&
    isBlank(form.database) &&
    isBlank(form.username) &&
    isBlank(form.testTable)
  );
}

/**
 * Pull absolute http(s) URLs from free text (prefer as base_url).
 */
function extractHttpUrls(text) {
  const urls = [];
  const re = /https?:\/\/[^\s"'<>)\]]+/gi;
  let m;
  while ((m = re.exec(text))) {
    const u = m[0].replace(/[.,;]+$/, "");
    if (u.length > 10) urls.push(u);
  }
  return [...new Set(urls)];
}

function extractLabeledBaseUrl(text) {
  const raw = String(text || "");
  if (!raw.trim()) return "";
  const sep = String.raw`\s*(?:[=:]|\s-\s)\s*`;
  const labeled = raw.match(
    new RegExp(String.raw`\bbase[\s_-]?url${sep}(https?:\/\/[^\s,;]+)`, "i")
  );
  if (labeled) return labeled[1].replace(/[.,;]+$/, "");
  return "";
}

function endpointPathKey(path) {
  const raw = String(path || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      return u.pathname + (u.search || "");
    } catch {
      return raw;
    }
  }
  return raw.startsWith("/") ? raw : `/${raw}`;
}

function intakeEndpointsToFormRows(parsed, startIndex = 0) {
  return parsed.map((ep, i) => ({
    id: `ep-${startIndex + i + 1}`,
    method: normalizeEndpointMethod(ep.method, { allowEmpty: true }),
    path: ep.path,
    operation: ep.operation || "",
    purpose: ep.operation || "",
    requestBody: "",
    payloads: [],
  }));
}

/** Fill blank or gate0-default GET methods from FR-parsed endpoints; append FR-only paths. */
function mergeGate0EndpointsWithIntake(gate0Endpoints, parsedFromText) {
  if (!parsedFromText.length) {
    return gate0Endpoints;
  }
  const frByPath = new Map();
  for (const ep of parsedFromText) {
    const key = endpointPathKey(ep.path);
    if (key && !frByPath.has(key)) frByPath.set(key, ep);
  }

  const seenPaths = new Set();
  const merged = (gate0Endpoints || []).map((row) => {
    const key = endpointPathKey(row.path);
    if (key) seenPaths.add(key);
    const fr = key ? frByPath.get(key) : null;
    const gateMethod = String(row.method || "").trim();
    const frMethod = fr?.method
      ? normalizeEndpointMethod(fr.method, { allowEmpty: true })
      : "";
    let method = gateMethod;
    if (!method && frMethod) {
      method = frMethod;
    } else if (method === "GET" && frMethod && frMethod !== "GET") {
      method = frMethod;
    }
    const operation =
      String(row.operation || "").trim() || String(fr?.operation || "").trim();
    return {
      ...row,
      method: normalizeEndpointMethod(method, { allowEmpty: true }),
      operation,
      purpose: operation || row.purpose || "",
    };
  });

  const extras = parsedFromText.filter((ep) => {
    const key = endpointPathKey(ep.path);
    return key && !seenPaths.has(key);
  });
  if (extras.length) {
    merged.push(...intakeEndpointsToFormRows(extras, merged.length));
  }
  return merged;
}

function deriveBaseUrlFromUrls(urls) {
  for (const u of urls) {
    try {
      const parsedUrl = new URL(u);
      return `${parsedUrl.protocol}//${parsedUrl.host}`;
    } catch {
      /* ignore */
    }
  }
  return "";
}

/** Remove wrapping quotes and trailing punctuation from a parsed path segment. */
function normalizeParsedEndpointPath(pathOrUrl) {
  return String(pathOrUrl || "")
    .trim()
    .replace(/^["']+/, "")
    .replace(/["']+$/, "")
    .replace(/[.,;]+$/, "");
}

/**
 * Parse "GET /v1/health" / "POST https://api.x.com/v1/foo" style lines.
 * @returns {{ method: string, path: string, operation: string, purpose: string }[]}
 */
export function parseEndpointsFromText(raw) {
  const text = String(raw || "").trim();
  if (!text) return [];

  // Try JSON array first
  if (text.startsWith("[") || text.startsWith("{")) {
    try {
      const parsed = JSON.parse(text);
      const list = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.endpoints)
          ? parsed.endpoints
          : [];
      return list
        .map((ep) => {
          const operation = resolveEndpointOperation(ep);
          const rawMethod = ep.method != null ? String(ep.method).trim() : "";
          return {
            method: rawMethod ? rawMethod.toUpperCase() : "",
            path: normalizeParsedEndpointPath(ep.path || ep.url || ""),
            operation,
            purpose: operation,
          };
        })
        .filter((ep) => ep.path);
    } catch {
      /* fall through to line parse */
    }
  }

  const found = [];
  const seen = new Set();
  const methodAlt = METHODS.join("|");
  // GET /path … | GET https://host/path
  const lineRe = new RegExp(
    `\\b(${methodAlt})\\s+(?:["'])?((?:https?:\\/\\/[^\\s,;"']+)|(?:\\/[^\\s,;"']*))(?:["'])?`,
    "gi"
  );
  let m;
  while ((m = lineRe.exec(text))) {
    const method = m[1].toUpperCase();
    let pathOrUrl = normalizeParsedEndpointPath(m[2]);
    let path = pathOrUrl;
    if (/^https?:\/\//i.test(pathOrUrl)) {
      try {
        const u = new URL(pathOrUrl);
        path = u.pathname + (u.search || "");
      } catch {
        path = pathOrUrl;
      }
    }
    if (!path.startsWith("/") && !/^https?:\/\//i.test(path)) {
      path = `/${path}`;
    }
    const key = `${method} ${path}`;
    if (seen.has(key)) continue;
    seen.add(key);
    found.push({
      method,
      path,
      operation: "",
      purpose: "",
    });
  }

  // Bare paths without method (e.g. "/api/v1/health") — treat as GET
  if (found.length === 0) {
    const pathRe = /(?:^|[\s,;"'])(\/[a-zA-Z0-9._~!$&'()*+,;=:@%/{}\-]{2,})/g;
    while ((m = pathRe.exec(text))) {
      const path = normalizeParsedEndpointPath(m[1]);
      if (path.length < 2 || path.includes("://")) continue;
      const key = `GET ${path}`;
      if (seen.has(key)) continue;
      seen.add(key);
      found.push({
        method: "GET",
        path,
        operation: "",
        purpose: "",
      });
    }
  }

  return found.slice(0, 20);
}

/**
 * Infer AUTH_TYPE_OPTIONS value from free text.
 */
export function parseAuthTypeFromText(raw) {
  const t = String(raw || "").toLowerCase();
  if (!t.trim()) return "";
  if (/\b(none|public|no[\s-]?auth|unauthenticated)\b/.test(t)) return "None";
  // API keys / SSO / OAuth are entered as Bearer Token in the readiness form.
  if (/\bapi[\s_-]?key\b/.test(t)) return "Bearer Token";
  if (/\b(bearer|jwt|oauth\s*2(\.0)?)\b/.test(t)) return "Bearer Token";
  if (/\bsso\b/.test(t)) return "Bearer Token";
  return "";
}

/**
 * Best-effort SQL connection hints from FR data answers.
 * Accepts common free-text shapes, e.g.:
 *   "Db name - validaite_dev, host - 44.215.169.3:5432, username - admin"
 *   "host=db.example.com; database=app; user=admin; port=5432"
 */
export function parseDbHintsFromText(raw) {
  const text = String(raw || "");
  if (!text.trim()) return {};

  const out = {};
  const lower = text.toLowerCase();

  if (/\bmysql\b/.test(lower)) out.dbType = "MySQL";
  else if (/\b(postgres|postgresql|rds|aurora)\b/.test(lower)) out.dbType = "PostgreSQL";

  // Separator: = : or " - " (common in FR answers)
  const sep = String.raw`\s*(?:[=:]|\s-\s)\s*`;

  const hostEq = text.match(
    new RegExp(String.raw`\bhost${sep}([^\s,;]+)`, "i")
  );
  if (hostEq) out.host = hostEq[1].replace(/['"]/g, "");

  // db.example.com / xxx.rds.amazonaws.com
  if (!out.host) {
    const hostLike = text.match(
      /\b([a-z0-9][a-z0-9.-]+\.(?:rds\.amazonaws\.com|azure\.com|googleapis\.com|internal|local|localhost))\b/i
    );
    if (hostLike) out.host = hostLike[1];
  }
  // Bare IPv4 when labeled nearby or alone with a db name
  if (!out.host) {
    const ip = text.match(/\b(\d{1,3}(?:\.\d{1,3}){3})(?::(\d{2,5}))?\b/);
    if (ip) {
      out.host = ip[1];
      if (ip[2]) out.port = ip[2];
    }
  }
  if (!out.host && /\blocalhost\b/i.test(text)) out.host = "localhost";

  // host may be "ip:port" or "hostname:5432"
  if (out.host && /:\d{2,5}$/.test(out.host)) {
    const parts = out.host.split(":");
    const maybePort = parts.pop();
    out.host = parts.join(":");
    if (!out.port) out.port = maybePort;
  }

  const portM = text.match(new RegExp(String.raw`\bport${sep}(\d{2,5})\b`, "i"));
  if (portM) out.port = portM[1];

  const dbEq = text.match(
    new RegExp(
      String.raw`\b(?:database(?:\s*name)?|db(?:[\s_-]?name)?|dbname)${sep}([a-zA-Z0-9_-]+)`,
      "i"
    )
  );
  if (dbEq) out.database = dbEq[1];

  const userEq = text.match(
    new RegExp(
      String.raw`\b(?:user(?:\s*name)?|username|db[\s_-]?user)${sep}([a-zA-Z0-9_@.-]+)`,
      "i"
    )
  );
  if (userEq) out.username = userEq[1].replace(/['"]/g, "");

  const tableEq = text.match(
    new RegExp(String.raw`\b(?:table|test[\s_-]?table)${sep}([a-zA-Z0-9_.]+)`, "i")
  );
  if (tableEq) out.testTable = tableEq[1];

  return out;
}

function captureBlob(captured, ...ids) {
  return ids.map((id) => captureValue(captured, id)).filter(Boolean).join("\n\n");
}

/**
 * Build API form: gate0 first, then fill blanks from FR_S6_Q01 only.
 * FR_S6_Q02 describes external dependencies and must not supply this app's base URL or endpoints.
 * Auth type is never taken from FR_S6_Q03.
 */
export function hydrateApiFormFromIntake(gate0 = {}, captured = {}) {
  const base = apiFormFromGate0Data(gate0);
  const apiText = captureBlob(captured, FR_API_EXPOSED);

  const urls = extractHttpUrls(apiText);
  const parsed = parseEndpointsFromText(apiText);
  let baseUrl = String(base.baseUrl || "").trim();
  if (!baseUrl) {
    baseUrl = extractLabeledBaseUrl(apiText);
  }
  if (!baseUrl && urls.length) {
    baseUrl = deriveBaseUrlFromUrls(urls);
  }
  if (!baseUrl && parsed.length) {
    for (const ep of parsed) {
      const p = String(ep.path || "");
      if (/^https?:\/\//i.test(p)) {
        try {
          const u = new URL(p);
          baseUrl = `${u.protocol}//${u.host}`;
          break;
        } catch {
          /* ignore */
        }
      }
    }
  }

  let endpoints = Array.isArray(base.endpoints) ? [...base.endpoints] : [];
  const hasPath = endpoints.some((ep) => String(ep?.path || "").trim());
  if (parsed.length) {
    endpoints = hasPath
      ? mergeGate0EndpointsWithIntake(endpoints, parsed)
      : intakeEndpointsToFormRows(parsed);
  }

  // Auth type is never auto-filled — user must pick from the dropdown.
  if (!endpoints.length) {
    endpoints = JSON.parse(JSON.stringify(EMPTY_API_FORM.endpoints));
  }

  return {
    baseUrl,
    // Intake text must not choose the dropdown. A saved gate0 auth_type is restored later.
    authType: "",
    endpoints,
  };
}

/**
 * Build DB form: gate0 first, then fill blanks from FR_S4_Q03 / Q01.
 * Falls back to scanning all captured answers when the FR ids are missing
 * or the DB details were written into a different item.
 */
export function hydrateDbFormFromIntake(gate0 = {}, captured = {}) {
  const base = dbFormFromGate0Data(gate0);
  let text = captureBlob(captured, FR_DATA_RETENTION, FR_DATA_INPUTS);
  let hints = parseDbHintsFromText(text);

  if (isBlank(hints.host) && isBlank(hints.database) && isBlank(hints.username)) {
    const allText = Object.keys(captured || {})
      .map((id) => captureValue(captured, id))
      .filter(Boolean)
      .join("\n\n");
    if (allText.trim()) {
      hints = parseDbHintsFromText(allText);
    }
  }

  return {
    // Intake text must not choose the dropdown. A saved gate0 db_type is restored later.
    dbType: "",
    host: String(base.host || hints.host || "").trim(),
    port: String(base.port || hints.port || "").trim(),
    database: String(base.database || hints.database || "").trim(),
    // Username is never auto-filled from free-text intake — too easy to pick up a person name.
    username: String(base.username || "").trim(),
    testTable: String(base.testTable || hints.testTable || "").trim(),
  };
}

/**
 * Intake progress stores a session snapshot of gate0_data that can be older than
 * the project row. Spread that snapshot on top, then put the project's saved
 * dropdowns back so an empty or stale session value cannot clear them.
 * Secrets already on either object are left as the spread left them.
 */
export function preserveSavedConnectionDropdowns(projectGate0 = {}, overlayGate0) {
  const project = projectGate0 && typeof projectGate0 === "object" ? projectGate0 : {};
  const hasOverlay = overlayGate0 && typeof overlayGate0 === "object";
  const merged = hasOverlay ? { ...project, ...overlayGate0 } : { ...project };

  const auth = String(project.auth_type || "").trim();
  if (auth) merged.auth_type = auth;

  const db = String(project.db_type || "").trim();
  if (db) merged.db_type = db;

  const projectStore =
    project.vector_store && typeof project.vector_store === "object" ? project.vector_store : {};
  const savedProvider = String(
    projectStore.provider || project.vector_provider || project.vectorProvider || ""
  ).trim();
  if (savedProvider) {
    const current =
      merged.vector_store && typeof merged.vector_store === "object" ? merged.vector_store : {};
    merged.vector_store = { ...current, provider: savedProvider };
  }

  return merged;
}

/**
 * Prefer existing non-empty gate0-derived form; otherwise FR hydrate.
 * If gate0 is partially filled, still fill remaining blanks from FR.
 * Saved auth_type is restored. Intake text does not choose the dropdown.
 */
export function resolveApiFormForReadiness(gate0 = {}, captured = {}) {
  const fromGate0 = apiFormFromGate0Data(gate0);
  const fromFr = hydrateApiFormFromIntake(gate0, captured);
  const authType = fromGate0.authType || "";
  if (apiFormIsEmpty(fromGate0)) {
    return { ...fromFr, authType };
  }

  return {
    baseUrl: String(fromGate0.baseUrl || fromFr.baseUrl || "").trim(),
    authType,
    endpoints: fromFr.endpoints,
  };
}

export function resolveDbFormForReadiness(gate0 = {}, captured = {}) {
  const fromGate0 = dbFormFromGate0Data(gate0);
  const fromFr = hydrateDbFormFromIntake(gate0, captured);
  const dbType = fromGate0.dbType || "";
  if (dbFormIsEmpty(fromGate0)) {
    return { ...fromFr, dbType, username: "" };
  }

  return {
    dbType,
    host: String(fromGate0.host || fromFr.host || "").trim(),
    port: String(fromGate0.port || fromFr.port || "").trim(),
    database: String(fromGate0.database || fromFr.database || "").trim(),
    username: "",
    testTable: String(fromGate0.testTable || fromFr.testTable || "").trim(),
  };
}

export {
  apiFormIsEmpty,
  dbFormIsEmpty,
  FR_API_EXPOSED,
  FR_AUTH,
  FR_DATA_RETENTION,
};
